/**
 * Operation Mapper - Backend to Frontend
 * @description Maps backend operation data (shortened fields) to frontend operation data (full fields)
 */

import { BackendOperation } from '@/types/backend.types';
import { OperationResponse } from '@/types/operation.types';

export class OperationMapper {
  /**
   * Map single backend operation to frontend operation
   */
  static toFrontend(backendOperation: BackendOperation): OperationResponse {
    return {
      id: backendOperation.id,
      operationIdentifier: backendOperation.opId,
      deviceId: backendOperation.devId,
      startTimestamp: backendOperation.start,
      endTimestamp: backendOperation.end,
      durationMinutes: undefined, // Not available in backend DTO
      timezone: backendOperation.tz
        ? {
            timezone: backendOperation.tz,
            offset: '+00:00', // Default offset
          }
        : {
            timezone: 'UTC',
            offset: '+00:00',
          },
      centroidLocation: backendOperation.centroid
        ? {
            latitude: backendOperation.centroid.lat,
            longitude: backendOperation.centroid.long,
          }
        : undefined,
      centroid: backendOperation.centroid
        ? {
            lat: backendOperation.centroid.lat,
            lng: backendOperation.centroid.long,
          }
        : undefined,
      angleLimits: undefined, // Not available in backend DTO
      weatherSummary: undefined, // Not available in backend DTO
      performance: undefined, // Not available in backend DTO
      metadata: {
        createdAt: backendOperation.createdAt,
        updatedAt: backendOperation.updatedAt,
        status: backendOperation.status,
      },
      createdAt: backendOperation.createdAt,
      updatedAt: backendOperation.updatedAt,
    };
  }

  /**
   * Map array of backend operations to frontend operations
   */
  static toFrontendArray(backendOperations: BackendOperation[]): OperationResponse[] {
    return backendOperations.map((operation) => OperationMapper.toFrontend(operation));
  }

  /**
   * Map frontend operation to backend operation (for create/update operations)
   */
  static toBackend(frontendOperation: Partial<OperationResponse>): Partial<BackendOperation> {
    const backendOperation: Partial<BackendOperation> = {};

    if (frontendOperation.id !== undefined) backendOperation.id = frontendOperation.id;
    if (frontendOperation.deviceId !== undefined)
      backendOperation.devId = frontendOperation.deviceId;
    if (frontendOperation.operationIdentifier !== undefined)
      backendOperation.opId = frontendOperation.operationIdentifier;
    if (frontendOperation.startTimestamp !== undefined)
      backendOperation.start = frontendOperation.startTimestamp;
    if (frontendOperation.endTimestamp !== undefined)
      backendOperation.end = frontendOperation.endTimestamp;
    if (frontendOperation.createdAt !== undefined)
      backendOperation.createdAt = frontendOperation.createdAt;
    if (frontendOperation.updatedAt !== undefined)
      backendOperation.updatedAt = frontendOperation.updatedAt;
    if (frontendOperation.timezone?.timezone !== undefined)
      backendOperation.tz = frontendOperation.timezone.timezone;
    if (frontendOperation.metadata?.status !== undefined)
      backendOperation.status = frontendOperation.metadata.status;

    if (frontendOperation.centroidLocation !== undefined) {
      backendOperation.centroid = {
        lat: frontendOperation.centroidLocation.latitude,
        long: frontendOperation.centroidLocation.longitude,
      };
    }

    return backendOperation;
  }

  /**
   * Map array of frontend operations to backend operations
   */
  static toBackendArray(
    frontendOperations: Partial<OperationResponse>[]
  ): Partial<BackendOperation>[] {
    return frontendOperations.map((operation) => OperationMapper.toBackend(operation));
  }
}
